import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../../utils/colors';
import { usePortalAuth } from '../../context/portalAuthContext';
import portalDataService from '../../services/portalDataService';

/**
 * Forced first-login password change (must_change_password === true). Blocks all
 * other portal navigation until it succeeds. On success we refresh /me so the
 * shell re-routes to the profile/pending/full portal.
 */
const PortalForceChangePassword = () => {
  const { t } = useTranslation();
  const s = t('portal.onboarding.forceChange', { returnObjects: true });
  const { customer, logout, refreshCustomer } = usePortalAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (!current || !next) { setError(s?.errors?.required || 'Enter your temporary and new password.'); return; }
    if (next.length < 8) { setError(s?.errors?.short || 'New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setError(s?.errors?.mismatch || 'New passwords do not match.'); return; }
    setBusy(true);
    try {
      const res = await portalDataService.changePassword(current, next);
      if (res.success) {
        // Password IS changed server-side now (must_change_password cleared).
        // Re-fetch /me so the shell re-routes. If that refresh fails (network
        // blip), the component stays mounted — tell the user it succeeded and
        // to continue, rather than leaving them staring at the same form.
        const refreshed = await refreshCustomer();
        if (!refreshed?.success) {
          setError(s?.changedRefreshFailed || 'Password changed. Please refresh the page to continue.');
        }
        // On success the shell re-routes and this unmounts.
      } else {
        setError(res.error || (s?.errors?.failed) || 'Could not change the password.');
      }
    } catch (err) {
      setError((s?.errors?.failed) || 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = { backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: GRADIENTS.NEUTRAL, backgroundColor: UI_COLORS.BACKGROUNDS.PRIMARY }}>
      <div className="w-full max-w-md rounded-2xl p-8 shadow-2xl" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-1" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{s?.title || 'Set your password'}</h1>
          <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{s?.subtitle || 'For security, please change your temporary password before continuing.'}</p>
          {customer?.company_name && <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{customer.company_name}</p>}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.current || 'Temporary password'}</label>
            <input type="password" value={current} onChange={(e) => { setCurrent(e.target.value); if (error) setError(''); }} autoComplete="current-password" className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.new || 'New password'} ({s?.min8 || 'min 8 chars'})</label>
            <input type="password" value={next} onChange={(e) => { setNext(e.target.value); if (error) setError(''); }} autoComplete="new-password" className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{s?.confirm || 'Confirm new password'}</label>
            <input type="password" value={confirm} onChange={(e) => { setConfirm(e.target.value); if (error) setError(''); }} autoComplete="new-password" className="w-full px-3 py-2 border-2 rounded-lg text-sm" style={inputStyle} />
          </div>

          {error && <div className="rounded-lg px-3 py-2 text-sm font-medium" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1), color: UI_COLORS.STATUS.ERROR }}>{error}</div>}

          <button type="submit" disabled={busy} className="w-full py-3 rounded-xl font-semibold disabled:opacity-60" style={{ background: GRADIENTS.PRIMARY, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
            {busy ? (s?.saving || 'Saving…') : (s?.submit || 'Change password & continue')}
          </button>
        </form>

        <button type="button" onClick={logout} className="mt-4 w-full text-sm font-medium hover:underline" style={{ color: UI_COLORS.TEXT.ACCENT }}>
          {s?.logout || 'Log out'}
        </button>
      </div>
    </div>
  );
};

export default PortalForceChangePassword;
